import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { IProduct } from "@bca-shared/models/product";
import { NsShop, PageType, PageTypeValueType } from "@bca-shared/utils/avo/Avo";
import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { ButtonType } from "@bca-shared/utils/avo/avo-event-details.models";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import { WindowReferenceService } from "@bca-shared/utils/window-util/window.reference/window.reference.service";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AvoTrackingService {
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private _avoService: AvoService,
    private readonly _windowRefService: WindowReferenceService,
    private _avoTransformService: AvoDataTransformService,
  ) {}

  initializePageViewTracking() {
    this.router.events
      .pipe(
        takeUntil(this._destroy$),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        const routeData = this.getCurrentRouteData();
        this.sendTrackingEvent(routeData);
      });
  }

  triggerInitialPageView() {
    const initialRouteData = this.getCurrentRouteData();
    this.sendTrackingEvent(initialRouteData);
  }

  trackRemoveFromCart(product: IProduct): void {
    const eventProperties =
      this._avoTransformService.transformRemoveFromCartAction(product);

    this._avoService.removeFromCart(eventProperties);
  }

  trackDownloadFormLinkClick(clickDestination: string) {
    const websiteSection = this.getPageTitle(this.getCurrentRouteData());
    this._avoService.linkClick({
      newTab: true,
      externalLink: false,
      uniqueName: "link_click_form",
      description: "link click to aanvraagformulier",
      clickDestination: clickDestination,
      clickText: "ga naar het aanvraagformulier",
      websiteSection: websiteSection,
    });
  }

  trackBackLinkClick(clickDestination: string) {
    const websiteSection = this.getPageTitle(this.getCurrentRouteData());
    this._avoService.linkClick({
      newTab: false,
      externalLink: false,
      uniqueName: "back_click",
      description: "go_back_one_page",
      clickDestination: clickDestination,
      clickText: "terug",
      websiteSection: websiteSection,
    });
  }

  trackButtonClick(props) {
    const { uniqueName, description, clickDestination, clickText, data } =
      props;

    return {
      buttonType: ButtonType.SUBMIT,
      uniqueName: uniqueName,
      description: description,
      clickDestination: clickDestination,
      clickText: clickText,
      websiteSection: this.getPageTitle(this.getCurrentRouteData()),
      data: data,
    };
  }

  trackOrderChange(product: IProduct) {
    const websiteSection = this.getPageTitle(this.getCurrentRouteData());
    this._avoService.orderChange({
      uniqueName: "",
      description:"",
      clickDestination: "https://www.ns.nl/businesscardaanvragen/#/",
      websiteSection: websiteSection,
      existingProduct: [this._avoTransformService.transformItem(product)]
    })
  }

  trackAddExtraCardLinkClick(clickDestination: string) {
    const websiteSection = this.getPageTitle(this.getCurrentRouteData());
    this._avoService.linkClick({
      newTab: false,
      externalLink: false,
      uniqueName: "addExtraCard",
      description: "go_back_to_page_one",
      clickDestination: clickDestination,
      clickText: "Nog een kaart toevoegen",
      websiteSection: websiteSection,
    });
  }

  private getCurrentRouteData() {
    let route = this.activatedRoute.root;
    let routeData = null;

    while (route.firstChild) {
      route = route.firstChild;
      if (route.snapshot.data) {
        routeData = route.snapshot.data;
      }
    }

    return routeData;
  }

  private sendTrackingEvent(data: any) {
    const pagePath = this._windowRefService.nativeWindow.location.pathname;
    const pageTitle = this.getPageTitle(data);
    let pageType: PageTypeValueType;
    if (data.routeIndex === 5) {
      pageType = PageType.THANK_YOU_PAGE;
    } else {
      pageType = PageType.CHECK_OUT_PAGE;
    }

    this._avoService.pageView({
      pageType: pageType,
      pageStructure: pageTitle,
      pageTitle,
      pagePath,
      virtual: false,
      campaignStatus: undefined,
    });
  }

  private getPageTitle(data: any): string {
    switch (data.routeIndex) {
      case 0:
        return "ns-zakelijk|checkout|samenstellen";
      case 1:
        return "ns-zakelijk|checkout|2-gegevens-kaarthouder";
      case 2:
        return "ns-zakelijk|checkout|3-gegevens-bedrijf";
      case 3:
        return "ns-zakelijk|checkout|4-controle";
      case 4:
        return "ns-zakelijk|checkout|5-overeenkomst";
      case 5:
        return "thank_you_page";
      default:
        return "Onbekende pagina";
    }
  }
}
